<template>
  <div class="p2p-stack" style="gap:24px">
    <div v-if="!member" class="p2p-card is-accent">
      <div class="p2p-between">
        <div class="p2p-stack" style="gap:4px">
          <h2 class="p2p-h2">Your sample is ready</h2>
          <p class="p2p-text">Create an account to keep these posts, connect your accounts and publish them.</p>
        </div>
        <div class="p2p-row">
          <router-link :to="{ path: '/login', query: { next: $route.fullPath } }" class="p2p-btn ghost">Log in</router-link>
          <router-link :to="{ path: '/register', query: { next: $route.fullPath } }" class="p2p-btn">Save my posts</router-link>
        </div>
      </div>
    </div>

    <div v-if="learned" :class="['p2p-learned', { low: learned.confidence === 'low' }]">
      <span class="p2p-kicker">What we learned</span>
      <span class="p2p-text"><strong>{{ learned.name }}</strong><span v-if="learned.one_liner"> · {{ learned.one_liner }}</span></span>
      <span v-if="learned.confidence === 'low'" class="p2p-faint">We found little on the site, so please check this summary. One correction improves every post.</span>
      <button type="button" class="p2p-btn sm ghost" style="align-self:flex-start;padding:0 6px" @click="$emit('go', 'formats', { edit: '1' })">
        {{ learned.confidence === 'low' ? 'Check and correct it' : 'Not quite right? Correct it' }}
      </button>
    </div>

    <div class="p2p-between">
      <div class="p2p-stack" style="gap:6px">
        <h2 class="p2p-h2">{{ writing ? 'Writing your posts' : 'Pick what goes out' }}</h2>
        <p class="p2p-muted" style="margin:0">{{ subtitle }}</p>
      </div>
      <div class="p2p-row">
        <select v-if="batches.length > 1" :value="batchId" class="p2p-select" style="width:auto" aria-label="Batch" @change="selectBatch($event.target.value)">
          <option v-for="batch in batches" :key="batch.id" :value="String(batch.id)">{{ batchLabel(batch) }}</option>
        </select>
        <button v-if="member" type="button" class="p2p-btn outline" @click="$emit('go', 'formats')">Generate more</button>
      </div>
    </div>

    <div v-if="batchOperation && batchOperation.status === 'failed'" class="p2p-alert bad">
      <div class="p2p-stack" style="gap:8px">
        <strong>These posts could not be written.</strong>
        <span>{{ batchOperation.error || 'Please try again.' }} Your credits were not used.</span>
        <button type="button" class="p2p-btn sm soft" style="align-self:flex-start" @click="$emit('go', 'formats')">Try again</button>
      </div>
    </div>
    <div v-else-if="batchOperation && batchOperation.status === 'needs_credits'" class="p2p-alert">
      <template v-if="batchOperation.error">{{ batchOperation.error }} <router-link to="/subscribe">Choose a plan</router-link></template>
      <template v-else>Not enough credits to write this batch. <router-link :to="creditsLink">Add credits</router-link> and generate again.</template>
    </div>
    <div v-else-if="batchOperation && batchOperation.status === 'cancelled'" class="p2p-alert">
      <div class="p2p-stack" style="gap:8px">
        <strong>You cancelled this batch.</strong>
        <span>Nothing was written and your credits were not used.</span>
        <button type="button" class="p2p-btn sm soft" style="align-self:flex-start" @click="$emit('go', 'formats')">Choose formats</button>
      </div>
    </div>

    <div v-if="writing" class="p2p-grid">
      <div v-for="n in expected" :key="n" class="p2p-card">
        <div class="p2p-skeleton" style="height:18px;width:40%"></div>
        <div class="p2p-skeleton" style="height:220px"></div>
        <div class="p2p-skeleton" style="height:14px"></div>
        <div class="p2p-skeleton" style="height:14px;width:80%"></div>
      </div>
      <div class="p2p-card raised" style="justify-content:center">
        <div class="p2p-row"><span class="p2p-dot is-accent pulse"></span><strong>{{ batchOperation && batchOperation.stage ? batchOperation.stage : 'Writing your posts' }}</strong></div>
        <p class="p2p-muted" style="margin:0">Each post gets its own hook and only uses facts from your site. Visuals start as soon as the words are ready.</p>
        <p v-if="elapsed" class="p2p-faint p2p-mono" style="margin:0">{{ elapsed }} elapsed</p>
        <button v-if="member && batchOperation && batchOperation.job_id && batchOperation.status !== 'complete'" type="button" class="p2p-btn sm ghost"
                style="align-self:flex-start" :disabled="stopping" @click="cancelBatch">{{ stopping ? 'Cancelling…' : 'Cancel this batch' }}</button>
        <span v-if="stopNote" class="p2p-faint">{{ stopNote }}</span>
      </div>
    </div>

    <PostGrid v-else-if="posts.length" v-slot="{ cell }" :count="posts.length">
      <PostCard v-for="(post, index) in posts" :key="post.id" :post="post" :images="images" :platforms="platforms" :prices="prices"
                :ai-video="aiVideo" :readonly="!member" :working="working[post.id] || []" :failure="failures[post.id] || ''"
                :wide="cell(index).wide" :style="cell(index).style"
                @updated="replace" @operation="track(post, $event)" @delete="removePost" @notice="notice = $event" />
    </PostGrid>
    <div v-else-if="!batchOperation" class="p2p-empty">
      <strong>No posts yet.</strong>
      <button type="button" class="p2p-btn" @click="$emit('go', 'formats')">Choose formats</button>
    </div>

    <div v-if="posts.length && member" class="p2p-sticky">
      <span><strong>{{ selected.length }}</strong> of {{ posts.length }} posts selected<span v-if="pendingVisuals" class="p2p-faint"> · {{ pendingVisuals }} visual{{ pendingVisuals === 1 ? '' : 's' }} still in progress</span></span>
      <button type="button" class="p2p-btn lg" :disabled="!selected.length" @click="continueToAccounts">Choose accounts <v-icon small color="#0b0b10">mdi-arrow-right</v-icon></button>
    </div>
    <v-snackbar v-model="showNotice" :timeout="2500" bottom>{{ notice }}</v-snackbar>
  </div>
</template>

<script>
import PostCard from '../PostCard.vue';
import PostGrid from '../PostGrid.vue';
import { get, remove, stopJob, message } from '../../api';
import { poller, active, localDate } from '../../poll';

export default {
  name: 'StepReview',
  components: { PostCard, PostGrid },
  props: { project: { type: Object, required: true }, config: { type: Object, default: null }, member: { type: Boolean, default: false } },
  data: () => ({ posts: [], batchOperation: null, batchId: '', loop: null, tracked: {}, failures: {}, notice: '', error: '',
    stopping: false, stopNote: '', clock: Date.now(), fetchedAt: Date.now(), ticker: null }),
  computed: {
    batches() { return this.project.batches || []; },
    // Top-ups are for members with a plan (on Billing); everyone else starts with a plan.
    creditsLink() { const w = this.$store.getters['publisher/workspace']; return w && w.paid_access ? '/billing' : '/subscribe'; },
    learned() { return this.project.knowledge_ready ? this.project.knowledge : null; },
    writing() { return !!(this.batchOperation && active(this.batchOperation)) || !!(this.batchOperation && this.batchOperation.status === 'complete' && !this.batchOperation.applied); },
    expected() {
      const batch = this.batches.find(item => String(item.id) === this.batchId);
      return batch ? Math.min(batch.requested || 3, 6) : 3;
    },
    subtitle() {
      if (this.writing) return 'This takes about a minute. Visuals appear as they are designed.';
      const openings = this.batchOperation && this.batchOperation.openings;
      const best = openings && openings.considered > this.posts.length
        ? 'We wrote ' + openings.considered + ' openings and kept the strongest for each post. ' : '';
      return best + 'These are previews: each network shows a post a little differently, as noted under it. Edit anything, rewrite with one click, and leave out what you do not want. Nothing is posted yet.';
    },
    images() {
      const knowledge = this.project.knowledge;
      const seen = (knowledge && knowledge.uploads_seen) || {};
      const site = knowledge ? knowledge.images.map(image => ({ id: image.id, url: image.url, alt: image.alt || 'From your site', seen: image.seen || '' })) : [];
      const uploads = (this.project.sources || []).filter(source => source.kind === 'image' && source.media)
        .map(source => ({ id: source.ref, url: source.media.poster_url || source.media.url, alt: source.filename, seen: seen[source.ref] || '' }));
      return site.concat(uploads);
    },
    platforms() {
      const accounts = this.$store.getters['publisher/connected'];
      const seen = [];
      accounts.forEach(account => { if (seen.indexOf(account.platform) < 0) seen.push(account.platform); });
      return seen;
    },
    prices() { return this.config ? this.config.prices : undefined; },
    aiVideo() { const w = this.$store.getters['publisher/workspace']; return !!(this.config && this.config.ai_video_available && w && w.ai_video_access); },
    selected() { return this.posts.filter(post => post.selected); },
    elapsed() {
      const op = this.batchOperation;
      if (!op || !this.writing || typeof op.elapsed_seconds !== 'number') return '';
      const seconds = Math.max(0, Math.round(op.elapsed_seconds + (this.clock - this.fetchedAt) / 1000));
      return seconds < 60 ? seconds + 's' : Math.floor(seconds / 60) + 'm ' + (seconds % 60) + 's';
    },
    showNotice: { get() { return !!this.notice; }, set(value) { if (!value) this.notice = ''; } },
    pendingVisuals() { return this.posts.filter(post => post.visual_status === 'processing' || post.visual_status === 'pending').length; },
    working() {
      const map = {};
      (this.project.operations || []).filter(active).forEach(op => {
        if (op.post_id) { map[op.post_id] = (map[op.post_id] || []).concat([op.kind]); }
      });
      Object.keys(this.tracked).forEach(id => {
        const item = this.tracked[id];
        map[item.post] = (map[item.post] || []).concat([item.kind]);
      });
      return map;
    }
  },
  watch: {
    // Visuals are charged when they finish, so refresh the balance as they land.
    pendingVisuals(now, before) { if (now < before) this.$store.dispatch('publisher/loadWorkspace'); },
    writing(now, before) { if (before && !now) this.$store.dispatch('publisher/loadWorkspace'); },
    '$route.query.batch': { immediate: true, handler(value) {
      const fallback = this.batches.length ? String(this.batches[0].id) : '';
      const next = value ? String(value) : fallback;
      if (next !== this.batchId || !this.loop) {
        this.batchId = next;
        this.start();
      }
    } }
  },
  mounted() {
    if (this.member) this.$store.dispatch('publisher/loadAccounts').catch(() => {});
    this.ticker = setInterval(() => { this.clock = Date.now(); }, 1000);
  },
  beforeDestroy() { if (this.loop) this.loop.stop(); clearInterval(this.ticker); },
  methods: {
    selectBatch(value) { this.$router.replace({ query: { batch: value } }).catch(() => {}); },
    batchLabel(batch) { return (batch.kind === 'sample' ? 'Sample' : batch.requested + ' posts') + ' · ' + localDate(batch.created_at, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); },
    start() {
      if (this.loop) this.loop.stop();
      this.posts = [];
      this.batchOperation = null;
      if (!this.batchId) return;
      this.loop = poller(() => this.refresh(), 2500);
      this.loop.start();
    },
    async refresh() {
      const id = this.batchId;
      const operation = await get('/operations/' + id);
      if (id !== this.batchId) return false;
      this.batchOperation = operation;
      this.fetchedAt = Date.now();
      if (operation.status === 'complete' && operation.applied) {
        const data = await get('/projects/' + this.project.id + '/posts', { batch_id: id });
        if (id !== this.batchId) return false;
        this.posts = data.posts;
      }
      await this.checkTracked();
      const busy = this.writing || this.pendingVisuals > 0 || Object.keys(this.tracked).length > 0 ||
        (this.project.operations || []).some(active);
      if (busy && (this.project.operations || []).some(active)) this.$emit('refresh');
      return busy;
    },
    async checkTracked() {
      for (const id of Object.keys(this.tracked)) {
        const item = this.tracked[id];
        try {
          const op = await get('/operations/' + id);
          if (active(op)) continue;
          this.$delete(this.tracked, id);
          if (op.status === 'failed') this.$set(this.failures, item.post, op.error || 'That did not work. Your credits were not used.');
          else if (op.status === 'needs_credits') this.$set(this.failures, item.post, op.error || 'Not enough credits for that.');
        } catch (error) {
          this.$delete(this.tracked, id);
        }
      }
    },
    track(post, operation) {
      this.$delete(this.failures, post.id);
      this.$set(this.tracked, String(operation.id), { post: post.id, kind: operation.kind });
      this.$store.dispatch('publisher/loadWorkspace');
      if (!this.loop) this.start();
      else { this.loop.stop(); this.loop.start(); }
    },
    replace(updated) {
      const index = this.posts.findIndex(post => post.id === updated.id);
      if (index >= 0) this.$set(this.posts, index, updated);
      if (updated.visual_status === 'processing') { this.loop.stop(); this.loop.start(); }
    },
    async removePost(post) {
      try {
        await remove('/posts/' + post.id);
        this.posts = this.posts.filter(item => item.id !== post.id);
      } catch (error) {
        this.notice = message(error);
      }
    },
    async cancelBatch() {
      this.stopping = true;
      this.stopNote = '';
      try {
        const job = await stopJob(this.batchOperation.job_id);
        if (job.status === 'cancelling') this.stopNote = 'Stopping… a cancelled batch is never charged.';
        if (this.loop) { this.loop.stop(); this.loop.start(); }
      } catch (error) {
        this.stopNote = message(error);
      } finally {
        this.stopping = false;
      }
    },
    continueToAccounts() {
      this.$emit('go', 'accounts', { batch: this.batchId });
    }
  }
};
</script>

<style>
.p2p-learned{display:flex;flex-direction:column;gap:4px;padding:12px 16px;border:1px solid var(--p2p-line);border-radius:14px;background:var(--p2p-panel)}
.p2p-learned.low{border-color:rgba(251,191,36,.35)}
</style>
